import type { ReactNode } from 'react';
import { ArrowUpRight, Mail, Pencil, Plus, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { SheetLead } from '../../../hooks/useLeadSheet';
import { leadStatusLabel } from '../../../lib/crm/leadSheet';
import { BTN_GHOST, BTN_PRIMARY, BTN_SECONDARY } from '../../bureau/buttons';
import { REF } from '../../bureau/encre';

/**
 * The head of an onboarding lead's sheet (mock-up « Encre-Fiche »): its mark, the company's name,
 * « Cars du Nord · Abonné » — the account opens its own sheet —; then the gestures: call and write
 * its contact, give Otto a mission about the client (the blue), edit it here, open the full record.
 */
export function LeadSheetHeader({
  lead,
  call,
  email,
  newMissionHref,
  ficheHref,
  editing,
  onOpenAccount,
  onEdit,
  onNavigate,
}: {
  lead: SheetLead;
  call: ReactNode;
  email: string | null;
  newMissionHref: string;
  ficheHref: string;
  /** The form is open: no gesture that would leave it (the form saves or cancels). */
  editing: boolean;
  onOpenAccount: (id: string) => void;
  onEdit: () => void;
  onNavigate: () => void;
}) {
  const account = lead.account;
  // Its account usually bears its name: then the link says what it opens, not the name twice.
  const sameName = account && account.name.trim().toLowerCase() === (lead.row.company_name ?? '').trim().toLowerCase();
  const accountLabel = account ? (sameName ? 'Fiche du compte' : account.name) : '';
  return (
    <header className="flex flex-col gap-5 pb-6">
      <div className="flex items-center gap-4">
        <span aria-hidden="true" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-desk-accent-soft text-desk-accent-text">
          <Rocket size={24} strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-medium leading-8 tracking-[-0.02em] text-desk-ink">{lead.row.company_name || 'Lead sans nom'}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-desk-soft">
            {account &&
              (editing ? (
                <span>{accountLabel}</span>
              ) : (
                <button type="button" onClick={() => onOpenAccount(account.id)} className={REF}>
                  {accountLabel}
                </button>
              ))}
            {!account && <span className="text-desk-muted">Aucun compte rattaché</span>}
            <span aria-hidden="true">·</span>
            <span>Lead d'onboarding · {leadStatusLabel(lead.row.status).toLowerCase()}</span>
          </p>
        </div>
      </div>
      {/* While editing, nothing here leaves the form: its own buttons save or cancel. */}
      {!editing && (
        <div className="flex flex-wrap items-center gap-2">
          {call}
          {email && (
            <a href={`mailto:${email}`} className={BTN_SECONDARY}>
              <Mail size={16} aria-hidden="true" /> Écrire
            </a>
          )}
          <Link to={newMissionHref} onClick={onNavigate} className={BTN_PRIMARY}>
            <Plus size={16} aria-hidden="true" /> Nouvelle mission
          </Link>
          <button type="button" onClick={onEdit} className={BTN_GHOST}>
            <Pencil size={15} aria-hidden="true" /> Modifier
          </button>
          <Link to={ficheHref} onClick={onNavigate} className={BTN_GHOST}>
            Fiche complète <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        </div>
      )}
    </header>
  );
}
